import { cn, NoddiIcon } from 'noddi-ui-common';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../base/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../base/dropdown-menu';
import { useSidebar } from '../../../base/sidebar/sidebar-context';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../base/tooltip';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from './Sidebar';
import { SidebarDropdown, SidebarLink } from './types';
import { useMenuTooltip } from './useMenuTooltip';

// Utility function to generate unique ID from title
const generateIdFromTitle = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .trim();
};

const collapsedTooltipClassName = 'border border-border bg-white text-foreground shadow-md';
const activeParentClassName = 'bg-purpleBg font-semibold text-darkPurple hover:bg-purpleBg hover:text-darkPurple';

type CollapsedDropdownItemProps = {
  item: SidebarDropdown;
  itemId: string;
  currentPath: string;
  hasActiveChild: boolean;
  /** Runs when a sub-item is picked, so the section is already open once the sidebar expands. */
  onNavigate: () => void;
};

/** Icon-only sidebar: sub-items open in a flyout beside the icon instead of expanding the whole sidebar. */
function CollapsedDropdownItem({ item, itemId, currentPath, hasActiveChild, onNavigate }: CollapsedDropdownItemProps) {
  const { menu, content, tooltip } = useMenuTooltip();

  return (
    <SidebarMenuItem id={`nav-${itemId}`}>
      <DropdownMenu modal={false} {...menu}>
        <Tooltip {...tooltip}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              {/* aria-expanded, not data-state: the tooltip trigger overrides data-state on the same element */}
              <SidebarMenuButton className={hasActiveChild ? activeParentClassName : 'aria-expanded:bg-sidebar-accent'}>
                {item.icon && <NoddiIcon name={item.icon} />}
                <span>{item.title}</span>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side='right' className={collapsedTooltipClassName}>
            {item.title}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent side='right' align='start' sideOffset={8} className='min-w-52' {...content}>
          <DropdownMenuLabel className='text-xs font-semibold text-muted-foreground'>{item.title}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((subItem, subIndex) => {
            if (subItem.hide) {
              return null;
            }

            const isSubActive = currentPath === subItem.path;
            return (
              <DropdownMenuItem
                key={subIndex}
                asChild
                className={cn('cursor-pointer', isSubActive && 'bg-lightPurple font-semibold text-darkPurple')}
              >
                <Link to={subItem.path} id={`nav-${itemId}-${generateIdFromTitle(subItem.title)}`} onClick={onNavigate}>
                  <span className='truncate'>{subItem.title}</span>
                  {subItem.badge}
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

type NavigationItemProps = {
  item: SidebarDropdown | SidebarLink;
};

export function NavigationItem({ item }: NavigationItemProps) {
  const location = useLocation();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const currentPath = location.pathname;
  const hasSubItems = 'items' in item && item.items && item.items.length > 0;
  const isActive = 'path' in item && item.path ? currentPath === item.path : false;
  const hasActiveChild =
    'items' in item && item.items ? item.items.some((subItem) => subItem.path && currentPath === subItem.path) : false;
  const itemId = generateIdFromTitle(item.title);
  // `state` tracks the desktop sidebar; the mobile sheet always shows full rows.
  const isCollapsed = state === 'collapsed' && !isMobile;
  // Open the section that contains the active page on first render so the active child is visible
  const [collapsibleOpen, setCollapsibleOpen] = useState(hasActiveChild);

  if (item.hide) {
    return null;
  }

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  if (hasSubItems && isCollapsed) {
    return (
      <CollapsedDropdownItem
        item={item}
        itemId={itemId}
        currentPath={currentPath}
        hasActiveChild={hasActiveChild}
        onNavigate={() => setCollapsibleOpen(true)}
      />
    );
  }

  if (hasSubItems) {
    return (
      <SidebarMenuItem>
        <Collapsible
          className='group/collapsible'
          id={`nav-${itemId}`}
          open={collapsibleOpen}
          onOpenChange={setCollapsibleOpen}
        >
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className={hasActiveChild ? activeParentClassName : undefined}>
              {item.icon && <NoddiIcon name={item.icon} />}
              <span>{item.title}</span>
              {item.badge}
              <div className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90'>
                <NoddiIcon name='ChevronRight' />
              </div>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent className='overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'>
            <SidebarMenuSub>
              {item.items.map((subItem, subIndex) => {
                if (subItem.hide) {
                  return null;
                }

                const isSubActive = subItem.path ? currentPath === subItem.path : false;
                const subItemId = generateIdFromTitle(subItem.title);
                return (
                  <SidebarMenuSubItem key={subIndex} id={`nav-${itemId}-${subItemId}`}>
                    <SidebarMenuSubButton asChild isActive={isSubActive}>
                      <Link to={subItem.path ?? '#'} onClick={handleLinkClick}>
                        <span>{subItem.title}</span>
                        {subItem.badge}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    );
  }

  const linkButton = (
    <SidebarMenuButton asChild isActive={isActive}>
      <Link to={'path' in item ? (item.path ?? '#') : '#'} onClick={handleLinkClick}>
        {item.icon && <NoddiIcon name={item.icon} />}
        <span>{item.title}</span>
        {item.badge}
      </Link>
    </SidebarMenuButton>
  );

  return (
    <SidebarMenuItem id={`nav-${itemId}`}>
      {isCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{linkButton}</TooltipTrigger>
          <TooltipContent side='right' className={collapsedTooltipClassName}>
            {item.title}
          </TooltipContent>
        </Tooltip>
      ) : (
        linkButton
      )}
    </SidebarMenuItem>
  );
}
